/**
 * client/hud/killfeed.ts — obituary (kill feed) message window: queue + timing (pure, no DOM).
 *
 * OWNER: HUD-UI-AUDIO agent.
 *
 * BO2 draws obituaries with the engine's game-message window 0 (CoD.GameMessages.AddObituaryWindow,
 * ui/t6/hud/gamemessages.lua), placed at L+13..L+277, B-249..B-149 in the safe area, ExtraSmall
 * font, left aligned [hud.md "Kill feed"]. Timing is ESTIMATE from the T5 engine (KisakBlack
 * src/client/cl_console.h:507-508 defaultGameMessageTimes {5,8,5} / line counts {4,1,7};
 * cl_console.cpp con_gameMsgWindowN* dvars: fade in 0.25 s, fade out 0.5 s, scroll 0.25 s;
 * Con_GetMessageAlpha, Con_DrawMessageWindowOldToNew):
 *  - 4 lines, each lives 5 s, fades in over 250 ms and out over the last 500 ms;
 *  - the newest line is at the bottom; while a line is younger than the scroll time the whole
 *    stack is pushed down by (1 - age/scroll) * lineHeight and slides up into place;
 *  - when a 5th line arrives, the oldest is forced to fade out now (cl_console.cpp:1403-1416).
 */

export const OBIT_LINES = 4;
export const OBIT_TIME_MS = 5000;
export const OBIT_FADE_IN_MS = 250;
export const OBIT_FADE_OUT_MS = 500;
export const OBIT_SCROLL_MS = 250;

export interface ObitLine<T> {
  data: T;
  start: number;
  end: number;
}

export interface ObitDrawLine<T> {
  data: T;
  /** Line index from the bottom (0 = newest), may be fractional during scroll. */
  slot: number;
  alpha: number;
}

export class KillFeedQueue<T> {
  readonly lines: ObitLine<T>[] = [];

  add(data: T, now: number): void {
    this.prune(now);
    this.lines.push({ data, start: now, end: now + OBIT_TIME_MS });
    // window full: force the oldest live lines to start fading now
    const live = this.lines.filter((l) => l.end - OBIT_FADE_OUT_MS > now);
    for (let i = 0; i < live.length - OBIT_LINES; i++) {
      const l = live[i]!;
      l.end = Math.min(l.end, now + OBIT_FADE_OUT_MS);
    }
  }

  prune(now: number): void {
    for (let i = this.lines.length - 1; i >= 0; i--) if (this.lines[i]!.end <= now) this.lines.splice(i, 1);
  }

  clear(): void { this.lines.length = 0; }

  /** Lines to draw this frame, newest first, with their vertical slot and alpha. */
  layout(now: number, out: ObitDrawLine<T>[] = []): ObitDrawLine<T>[] {
    this.prune(now);
    out.length = 0;
    // scroll offset: every line younger than the scroll time pushes the stack down
    let push = 0;
    for (const l of this.lines) {
      const age = now - l.start;
      if (age < OBIT_SCROLL_MS) push += 1 - Math.max(0, age) / OBIT_SCROLL_MS;
    }
    let slot = 0;
    for (let i = this.lines.length - 1; i >= 0; i--, slot++) {
      const l = this.lines[i]!;
      out.push({ data: l.data, slot: slot - push, alpha: obitAlpha(l, now) });
    }
    return out;
  }
}

/** Con_GetMessageAlpha with fadeIn == scrollTime (no "scrolls into place" delay). */
export function obitAlpha(l: { start: number; end: number }, now: number): number {
  let a = 1;
  if (l.end - now < OBIT_FADE_OUT_MS) a = (l.end - now) / OBIT_FADE_OUT_MS;
  const age = now - l.start;
  if (age < OBIT_FADE_IN_MS) a *= Math.max(0, age) / OBIT_FADE_IN_MS;
  return a < 0 ? 0 : a > 1 ? 1 : a;
}
